import React, { useState, useEffect, useCallback, useRef } from "react";
import toast from "react-hot-toast";
import "../css/Common-Styles.css";
import "../css/Common.css";
import "./css/MemberUserInfo.css";
import "./css/MemberUserInfo_v2.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faUserShield,
  faLayerGroup,
  faInfoCircle,
  faUserPlus,
  faSpinner,
  faChevronDown,
  faChevronRight,
  faTimes,
  faToggleOn,
  faToggleOff,
  faTrash,
  faEnvelopeOpenText,
  faEye,
  faSlidersH,
} from "@fortawesome/free-solid-svg-icons";
import { useUser } from "../context/UserContext";
import { useSubscription } from "../context/SubscriptionContext";
import { useAccountSetup } from "../context/AccountSetupContext";
import { useRoleCheck } from "../../../hooks/useRoleCheck";
import UserAPI from "../services/UserAPI";
import UserGroupAPI from "../services/UserGroupAPI";
import UserDetailsAPI from "../services/UserDetailsAPI";
import SubscriptionsAPI from "../services/SubscriptionsAPI";
import AccountAPI from "../services/AccountAPI";
import PendingVerificationAPI from "./services/PendingVerificationAPI";
import { useTranslation } from "../../../i18n/LanguageContext";
import UserForm from "./components/UserForm";
import UserDetails from "./components/UserDetails";
import UserListControls from "./components/UserListControls";
import AddMemberSheet from "./components/AddMemberSheet";
import NewMemberInfoModal from "./components/NewMemberInfoModal";
import PhoneChangeVerificationPopup from "./PhoneChangeVerificationPopup";
import ConfirmationModal from "../common/ConfirmationModal";
import LoadingSpinner from "../common/LoadingSpinner";

// Tab identifiers
const TAB_PROFILE = "profile";
const TAB_DETAILS = "details";

// ── Helpers ────────────────────────────────────────────────────────────────

function formatPhone(raw) {
  if (!raw) return raw;
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 11 && digits[0] === "1") {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
}

// ── Component ──────────────────────────────────────────────────────────────

function MemberUserInfo_v2({
  onUserSelect,
  setHeroContent,
  initialSection,
  areAnnouncementsVisible,
  // When true, always render the logged-in user's own solo profile editor
  // regardless of their roles. Used to embed the personal "User Profile"
  // section inside the Account Profile page for account owners/admins.
  forceSelfProfile = false,
}) {
  const {
    selectedUser: contextSelectedUser,
    loggedInUser,
    selectUser,
    updateLoggedInUser,
    isDemoSeat,
  } = useUser();

  const { isAccountOwner: isLoggedInUserAccountOwner } = useAccountSetup();
  const { canCreateUsers, isGroupAdmin, isAccountAdmin, isPlatformAdmin } =
    useRoleCheck();
  const { subscription, servicePlan, getMaxUsers, hasActiveSubscription } =
    useSubscription();
  const { t } = useTranslation();

  // Owner/admin = split pane; member = solo profile
  const isOwnerView =
    !forceSelfProfile &&
    (isLoggedInUserAccountOwner ||
      isGroupAdmin() ||
      isAccountAdmin() ||
      isPlatformAdmin());

  // ── Roster selection ───────────────────────────────────────────────────────
  const [rosterSelectedUser, setRosterSelectedUser] = useState(
    contextSelectedUser || null,
  );

  // User whose data is shown in the detail sheet
  const detailUser = isOwnerView ? rosterSelectedUser : loggedInUser;
  // ── Side sheet visibility ──────────────────────────────────────────────────
  const [sideSheetOpen, setSideSheetOpen] = useState(false);

  // ── Sheet display mode ─────────────────────────────────────────────────────
  // "profile" | "activity" | "dashboard" | "analyses" | "reports"
  const [sheetMode, setSheetMode] = useState("profile");

  // ── Action dropdown ────────────────────────────────────────────────────────
  const [openDropdownUuid, setOpenDropdownUuid] = useState(null);

  // ── Consent data for Monitor Activity ─────────────────────────────────────
  // (loaded by MemberActivityView when navigating to Hero 'Member Activity')

  // ── Accordion sections ─────────────────────────────────────────────────────
  const [expandedSections, setExpandedSections] = useState(
    new Set([TAB_PROFILE]),
  );

  const toggleSection = (section) => {
    setExpandedSections((prev) => {
      if (prev.has(section)) return new Set();
      return new Set([section]);
    });
  };

  // ── Roster / list state ────────────────────────────────────────────────────
  const [users, setUsers] = useState([]);
  const [accountOwners, setAccountOwners] = useState({});
  const [actionInProgress, setActionInProgress] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [listRefreshing, setListRefreshing] = useState(false);

  // ── Detail pane state ──────────────────────────────────────────────────────
  const [updateFormData, setUpdateFormData] = useState({
    uuid: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    groupName: "",
  });
  const [userDetails, setUserDetails] = useState({
    userDetailId: null,
    uuid: null,
    city: "",
    state: "",
    country: "",
    ageRange: "",
    profession: "",
    hobby: "",
    additionalInfo: "",
  });
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [prominentWarning, setProminentWarning] = useState(null);
  const [errors, setErrors] = useState({});
  const [originalEmail, setOriginalEmail] = useState("");
  const [originalPhone, setOriginalPhone] = useState("");
  const [originalPhoneVerified, setOriginalPhoneVerified] = useState(false);
  const [showPhoneVerificationPopup, setShowPhoneVerificationPopup] =
    useState(false);

  // ── Resend verification inline state ──────────────────────────────────────
  const [resendingFor, setResendingFor] = useState(null);
  const [resendResult, setResendResult] = useState(null);

  // ── Subscription info ──────────────────────────────────────────────────────
  const [subscriptionInfo, setSubscriptionInfo] = useState({
    loading: true,
    maxUsers: null,
    currentUsers: null,
    isAtLimit: false,
    isNearLimit: false,
    planName: null,
    tier: null,
    isHighestTier: false,
    error: null,
  });

  // ── Add Member sheet ───────────────────────────────────────────────────────
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    groupName: "",
  });
  const [createErrors, setCreateErrors] = useState({});
  const [createLoading, setCreateLoading] = useState(false);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState(null);

  // ── New member info modal ────────────────────────────────────────────────
  const [newMemberInfo, setNewMemberInfo] = useState({
    open: false,
    name: "",
    email: "",
  });

  // ── Confirmation modals ────────────────────────────────────────────────────
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    type: "danger",
    title: "",
    message: "",
    details: "",
    confirmText: "",
    onConfirm: null,
  });

  // ────────────────────────────────────────────────────────────────────────────
  // Data loading
  // ────────────────────────────────────────────────────────────────────────────

  const loadSubscriptionLimits = useCallback(async () => {
    try {
      setSubscriptionInfo((p) => ({ ...p, loading: true, error: null }));
      if (!subscription || !servicePlan)
        throw new Error("No active subscription");
      const maxUsers = getMaxUsers();
      const planName =
        servicePlan.displayName ||
        servicePlan.planName ||
        servicePlan.name ||
        "unknown";
      const tier = servicePlan.tier || 1;
      const allPlans =
        (await SubscriptionsAPI.getAllServicePlans())?.data || [];
      const maxTier = Math.max(
        ...allPlans.filter((p) => p.isActive !== false).map((p) => p.tier || 1),
      );
      const account = (await AccountAPI.getAccountByUserUuid())?.data;
      const currentUsers = account
        ? (await AccountAPI.getUserCountByAccountId(account.accountId))
            ?.userCount || 0
        : 0;
      const isAtLimit = currentUsers >= maxUsers;
      const isNearLimit =
        currentUsers >= maxUsers - 2 &&
        currentUsers > maxUsers / 2 &&
        !isAtLimit;
      setSubscriptionInfo({
        loading: false,
        maxUsers,
        currentUsers,
        isAtLimit,
        isNearLimit,
        planName,
        tier,
        isHighestTier: tier >= maxTier,
        error: null,
      });
    } catch (err) {
      setSubscriptionInfo((p) => ({
        ...p,
        loading: false,
        error: err.message,
      }));
    }
  }, [subscription, servicePlan, getMaxUsers]);

  const fetchUsers = useCallback(async () => {
    try {
      setListLoading(true);
      const res = await UserAPI.getUsersInMyScope();
      const raw = res?.data || res || [];
      const usersWithGroup = raw.map((u) => ({
        ...u,
        groupName: u.groupName || "Unknown Group",
      }));
      setUsers(usersWithGroup);

      const ownerResults = await Promise.all(
        usersWithGroup.map(async (u) => {
          try {
            const r = await UserAPI.checkAccountOwnershipByUuid(u.uuid);
            return { uuid: u.uuid, isOwner: r.isAccountOwner === true };
          } catch {
            return { uuid: u.uuid, isOwner: false };
          }
        }),
      );
      const ownerMap = {};
      ownerResults.forEach(({ uuid, isOwner }) => {
        ownerMap[uuid] = isOwner;
      });
      setAccountOwners(ownerMap);

      setRosterSelectedUser((prev) => {
        if (prev) return prev;
        const own = usersWithGroup.find((u) => u.uuid === loggedInUser?.uuid);
        return own || usersWithGroup[0] || null;
      });
    } catch (err) {
      console.error("fetchUsers error", err);
    } finally {
      setListLoading(false);
    }
  }, [loggedInUser?.uuid, isAccountAdmin, isLoggedInUserAccountOwner]);

  const loadDetailForUser = useCallback(
    async (targetUuid) => {
      if (!targetUuid) return;
      setDetailLoading(true);
      setDetailError("");
      setProminentWarning(null);
      setErrors({});
      try {
        const userData = await UserAPI.getUserByUuid(targetUuid);
        const data = userData?.data !== undefined ? userData.data : userData;
        if (data) {
          setUpdateFormData({
            uuid: data.uuid || targetUuid,
            userId: data.userId || "",
            firstName: data.firstName || "",
            lastName: data.lastName || "",
            email: data.email || "",
            phone: data.phone || "",
            groupName: data.groupName || "",
            active: data.active || false,
            emailVerified: data.emailVerified || data.email_verified || false,
            phoneVerified: data.phoneVerified || data.phone_verified || false,
            pendingEmail: data.pendingEmail || data.pending_email,
            pendingPhone: data.pendingPhone || data.pending_phone,
            pendingEmailExpires:
              data.pendingEmailExpires || data.pending_email_expires,
            pendingPhoneExpires:
              data.pendingPhoneExpires || data.pending_phone_expires,
          });
          setOriginalEmail(data.email || "");
          setOriginalPhone(data.phone || "");
          setOriginalPhoneVerified(
            data.phoneVerified || data.phone_verified || false,
          );
        }

        const isSelf = targetUuid === loggedInUser?.uuid;
        const details = await UserDetailsAPI.getUserDetails(
          isSelf ? null : targetUuid,
        );
        setUserDetails({
          userDetailId: details?.userDetailId || null,
          uuid: targetUuid,
          city: details?.city || "",
          state: details?.state || "",
          country: details?.country || "",
          ageRange: details?.ageRange || "",
          profession: details?.profession || "",
          hobby: details?.hobby || "",
          additionalInfo: details?.additionalInfo || "",
        });
      } catch (err) {
        setDetailError(t("usermgmt.error.loadUserFailed"));
      } finally {
        setDetailLoading(false);
      }
    },
    [loggedInUser?.uuid, t],
  );

  const loadAvailableGroups = useCallback(async () => {
    try {
      setGroupsLoading(true);
      setGroupsError(null);
      const account = (await AccountAPI.getAccountByUserUuid())?.data;
      if (!account) throw new Error("Account not found");
      const res = await UserGroupAPI.getUserGroupsByAccountId(
        account.accountId,
      );
      const groups = res?.data || res || [];
      setAvailableGroups(Array.isArray(groups) ? groups : []);
    } catch (err) {
      setGroupsError(err.message);
      setAvailableGroups([]);
    } finally {
      setGroupsLoading(false);
    }
  }, []);

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (isOwnerView) {
      fetchUsers();
      loadSubscriptionLimits();
    } else {
      loadDetailForUser(loggedInUser?.uuid);
    }
  }, [isOwnerView, loggedInUser?.uuid]);

  useEffect(() => {
    if (isOwnerView && rosterSelectedUser?.uuid) {
      setExpandedSections(new Set([TAB_PROFILE]));
      loadDetailForUser(rosterSelectedUser.uuid);
    }
  }, [rosterSelectedUser?.uuid]);

  useEffect(() => {
    if (contextSelectedUser && isOwnerView) {
      setRosterSelectedUser(contextSelectedUser);
    }
  }, [contextSelectedUser?.uuid]);

  // Close dropdown when clicking outside any .umv2-action-menu
  useEffect(() => {
    if (!openDropdownUuid) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest(".umv2-action-menu")) {
        setOpenDropdownUuid(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdownUuid]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleSelectUser = (user) => {
    if (!user.verified) return;
    setRosterSelectedUser(user);
    selectUser(user);
    setExpandedSections(new Set([TAB_PROFILE]));
    setSheetMode("profile");
    setSideSheetOpen(true);
  };

  const handleCloseSheet = () => {
    setSideSheetOpen(false);
    setSheetMode("profile");
  };

  const handleMonitorActivity = (user) => {
    selectUser(user);
    setHeroContent("Member Activity");
  };

  const handleToggleUserStatus = (user) => {
    const newActive = !(user.active === 1 || user.active === true);
    const isActivating = newActive;

    setConfirmModal({
      open: true,
      type: isActivating ? "warning" : "danger",
      title: isActivating
        ? t("usermgmt.modal.activateTitle")
        : t("usermgmt.modal.deactivateTitle"),
      message: isActivating
        ? t("usermgmt.modal.activateMessage")
            .replace("{firstName}", user.firstName)
            .replace("{lastName}", user.lastName)
        : t("usermgmt.modal.deactivateMessage")
            .replace("{firstName}", user.firstName)
            .replace("{lastName}", user.lastName),
      details: isActivating
        ? t("usermgmt.modal.activateDetails")
        : t("usermgmt.modal.deactivateDetails"),
      confirmText: isActivating
        ? t("usermgmt.modal.activateConfirm")
        : t("usermgmt.modal.deactivateConfirm"),
      onConfirm: () => confirmToggleStatus(user, newActive),
    });
  };

  const confirmToggleStatus = async (user, newActive) => {
    setConfirmModal((p) => ({ ...p, open: false }));
    const prev = user.active;
    setUsers((us) =>
      us.map((u) => (u.uuid === user.uuid ? { ...u, active: newActive } : u)),
    );
    try {
      setActionInProgress(true);
      const isSelf = user.uuid === loggedInUser?.uuid;
      if (isSelf) {
        newActive
          ? await UserAPI.activateCurrentUser()
          : await UserAPI.deactivateCurrentUser();
      } else {
        newActive
          ? await UserAPI.activateUserByUuid(user.uuid)
          : await UserAPI.deactivateUserByUuid(user.uuid);
      }
      toast.success(
        newActive
          ? t("usermgmt.toast.activateSuccess")
          : t("usermgmt.toast.deactivateSuccess"),
      );
    } catch {
      setUsers((us) =>
        us.map((u) => (u.uuid === user.uuid ? { ...u, active: prev } : u)),
      );
      toast.error(t("usermgmt.error.updateStatusFailed"));
    } finally {
      setActionInProgress(false);
    }
  };

  const handleDeleteUser = (user) => {
    setConfirmModal({
      open: true,
      type: "danger",
      title: t("usermgmt.modal.deleteTitle"),
      message: t("usermgmt.modal.deleteSingleMessage")
        .replace("{firstName}", user.firstName)
        .replace("{lastName}", user.lastName),
      details: t("usermgmt.modal.deleteDetails"),
      confirmText: t("usermgmt.modal.deleteTitle"),
      onConfirm: () => confirmDeleteUser(user),
    });
  };

  const confirmDeleteUser = async (user) => {
    setConfirmModal((p) => ({ ...p, open: false }));
    try {
      setActionInProgress(true);
      await UserAPI.deleteMultipleUsers([user.uuid]);
      setUsers((us) => us.filter((u) => u.uuid !== user.uuid));
      if (rosterSelectedUser?.uuid === user.uuid) {
        const own = users.find((u) => u.uuid === loggedInUser?.uuid);
        setRosterSelectedUser(own || null);
        setSideSheetOpen(false);
      }
      await loadSubscriptionLimits();
      toast.success(t("usermgmt.toast.deleteSuccess"));
    } catch {
      toast.error(t("usermgmt.error.deleteFailed"));
    } finally {
      setActionInProgress(false);
    }
  };

  const handleResendVerification = async (memberUser) => {
    try {
      setResendingFor(memberUser.uuid);
      setResendResult(null);
      const res = await UserAPI.resendMemberVerification(memberUser.uuid);
      const data = res?.data ?? res;
      if (data?.success) {
        setResendResult({
          type: "success",
          uuid: memberUser.uuid,
          message:
            data.message ||
            t("usermgmt.toast.resendSuccess").replace(
              "{email}",
              memberUser.email,
            ),
        });
        toast.success(
          t("usermgmt.toast.resendSuccess").replace(
            "{email}",
            memberUser.email,
          ),
        );
      } else {
        setResendResult({
          type: "error",
          uuid: memberUser.uuid,
          message: data?.message || t("usermgmt.toast.resendFailed"),
        });
        toast.error(data?.message || t("usermgmt.toast.resendFailed"));
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "An error occurred while resending the verification email.";
      setResendResult({ type: "error", uuid: memberUser.uuid, message: msg });
      toast.error(msg);
    } finally {
      setResendingFor(null);
    }
  };

  // ── Detail form handlers ───────────────────────────────────────────────────

  const validateForm = (data) => {
    const e = {};
    if (!data.firstName?.trim())
      e.firstName = t("usermgmt.validation.firstNameRequired");
    if (!data.lastName?.trim())
      e.lastName = t("usermgmt.validation.lastNameRequired");
    if (!data.email?.trim()) e.email = t("usermgmt.validation.emailRequired");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email))
      e.email = t("usermgmt.validation.emailInvalid");
    if (!data.phone?.trim()) e.phone = t("usermgmt.validation.phoneRequired");
    else if (!/^\+[1-9]\d{1,14}$/.test(data.phone))
      e.phone = t("usermgmt.validation.phoneInvalid");
    return e;
  };

  const handleUpdateInputChange = (e) => {
    const { name, value } = e.target;
    setUpdateFormData((p) => ({
      ...p,
      [name]: value,
      ...(name === "phone"
        ? {
            phoneVerified:
              value === originalPhone ? originalPhoneVerified : false,
          }
        : {}),
    }));
    if (prominentWarning) setProminentWarning(null);
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const handleDetailsInputChange = (e) => {
    const { name, value } = e.target;
    setUserDetails((p) => ({ ...p, [name]: value }));
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    setDetailError("");
    setProminentWarning(null);
    const ve = validateForm(updateFormData);
    if (Object.keys(ve).length > 0) {
      setErrors(ve);
      return;
    }
    setDetailLoading(true);
    try {
      const { uuid, ...data } = updateFormData;
      const phoneChanged = data.phone !== originalPhone;
      const emailChanged = data.email !== originalEmail;

      if (phoneChanged && !emailChanged) {
        const res = await PendingVerificationAPI.requestPhoneChange(data.phone);
        if (res) {
          setUpdateFormData((p) => ({ ...p, phone: originalPhone }));
          setShowPhoneVerificationPopup(true);
          toast(t("usermgmt.toast.phoneChangePending"), {
            duration: 5000,
            icon: "ℹ️",
          });
        }
      } else if (emailChanged && !phoneChanged) {
        await PendingVerificationAPI.requestEmailChange(data.email);
        setUpdateFormData((p) => ({ ...p, email: originalEmail }));
        setProminentWarning(
          <div className="security-warning-box">
            <div className="security-warning-header">
              <span className="security-warning-icon">📧</span>
              <strong className="security-warning-title">
                {t("usermgmt.warning.emailRequired.title")}
              </strong>
            </div>
            <p className="security-warning-message">
              {t("usermgmt.warning.emailRequired.message")}
            </p>
            <p className="security-warning-details">
              {t("usermgmt.warning.emailRequired.details")}
            </p>
          </div>,
        );
      } else if (phoneChanged && emailChanged) {
        setUpdateFormData((p) => ({
          ...p,
          email: originalEmail,
          phone: originalPhone,
        }));
        setProminentWarning(
          <div className="security-warning-box">
            <div className="security-warning-header">
              <span className="security-warning-icon">🔒</span>
              <strong className="security-warning-title">
                {t("usermgmt.warning.securityRequired.title")}
              </strong>
            </div>
            <p className="security-warning-message">
              {t("usermgmt.warning.securityRequired.message")}
            </p>
            <p className="security-warning-details">
              {t("usermgmt.warning.securityRequired.details")}
            </p>
          </div>,
        );
      } else {
        const isSelf = uuid === loggedInUser?.uuid;
        const payload = isSelf ? data : { ...data, uuid };
        await UserAPI.updateUser(payload);
        const updated = { ...detailUser, ...data };
        setRosterSelectedUser(updated);
        selectUser(updated);
        if (isSelf) updateLoggedInUser(updated);
        setUsers((us) =>
          us.map((u) => (u.uuid === uuid ? { ...u, ...data } : u)),
        );
        toast.success(
          isSelf
            ? t("usermgmt.toast.updateSelf")
            : t("usermgmt.toast.updateOther").replace(
                "{firstName}",
                data.firstName || "User",
              ),
          { duration: 4000, icon: "✅" },
        );
      }
    } catch (err) {
      if (!err?.silent) {
        const message =
          err?.response?.data?.message ||
          err?.message ||
          t("usermgmt.error.updateError");
        toast.error(message);
      }
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDetailsSubmit = async (e) => {
    e.preventDefault();
    setDetailLoading(true);
    try {
      const res = userDetails.userDetailId
        ? await UserDetailsAPI.updateUserDetails(userDetails)
        : await UserDetailsAPI.createUserDetails(userDetails);
      if (
        res &&
        (res.success === true || res.success === undefined || !res.error)
      ) {
        const isSelf = userDetails.uuid === loggedInUser?.uuid;
        toast.success(
          isSelf
            ? t("usermgmt.toast.detailsSelf")
            : t("usermgmt.toast.detailsOther").replace(
                "{firstName}",
                detailUser?.firstName || "User",
              ),
          { duration: 4000, icon: "✅" },
        );
      } else {
        setDetailError(res?.message || t("usermgmt.error.saveDetailsFailed"));
      }
    } catch {
      setDetailError(t("usermgmt.error.saveDetailsError"));
    } finally {
      setDetailLoading(false);
    }
  };

  // ── Add Member handlers ────────────────────────────────────────────────────

  const handleOpenAddSheet = () => {
    loadAvailableGroups();
    setShowAddSheet(true);
  };

  const handleCreateInputChange = (e) => {
    const { name, value } = e.target;
    setCreateFormData((p) => ({ ...p, [name]: value }));
    if (createErrors[name]) setCreateErrors((p) => ({ ...p, [name]: "" }));
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    const ve = validateForm(createFormData);
    if (!createFormData.groupName?.trim())
      ve.groupName = t("usermgmt.validation.groupRequired");
    if (Object.keys(ve).length > 0) {
      setCreateErrors(ve);
      return;
    }
    setCreateLoading(true);
    try {
      const res = await UserAPI.createUser(createFormData, true);
      if (
        res &&
        (res.success === true || res.success === undefined || !res.error)
      ) {
        const createdName =
          `${createFormData.firstName} ${createFormData.lastName}`.trim();
        const createdEmail = createFormData.email;
        setCreateFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          groupName: "",
        });
        setCreateErrors({});
        setShowAddSheet(false);
        setNewMemberInfo({
          open: true,
          name: createdName,
          email: createdEmail,
        });
        await fetchUsers();
        await loadSubscriptionLimits();
      } else {
        toast.error(
          res?.message || res?.error || t("usermgmt.error.createFailed"),
        );
      }
    } catch (err) {
      toast.error(
        err.response?.status === 403
          ? t("usermgmt.error.unauthorized")
          : t("usermgmt.error.createError"),
      );
    } finally {
      setCreateLoading(false);
    }
  };

  // ── Derived values ─────────────────────────────────────────────────────────

  const sortedUsers = [...users].sort((a, b) => {
    const aOwner = accountOwners[a.uuid] || false;
    const bOwner = accountOwners[b.uuid] || false;
    if (aOwner && !bOwner) return -1;
    if (!aOwner && bOwner) return 1;
    return `${a.firstName} ${a.lastName}`.localeCompare(
      `${b.firstName} ${b.lastName}`,
    );
  });

  // ── Side sheet content ─────────────────────────────────────────────────────

  const renderSideSheetBody = () => {
    // ── Default: Profile + Details accordions ──────────────────────────────
    return (
      <div className="umv2-sheet-body">
        {detailError && (
          <div className="common-message error umv2-sheet-error">
            <FontAwesomeIcon icon={faInfoCircle} />
            <span>{detailError}</span>
          </div>
        )}
        {prominentWarning && (
          <div style={{ padding: "0 1.25rem" }}>{prominentWarning}</div>
        )}

        {/* Profile section */}
        <div className="umv2-section">
          <button
            className={`umv2-section-header${expandedSections.has(TAB_PROFILE) ? " expanded" : ""}`}
            onClick={() => toggleSection(TAB_PROFILE)}
            aria-expanded={expandedSections.has(TAB_PROFILE)}
          >
            <FontAwesomeIcon
              icon={
                expandedSections.has(TAB_PROFILE)
                  ? faChevronDown
                  : faChevronRight
              }
              className="umv2-section-chevron"
            />
            <span className="umv2-section-title">
              {t("usermgmt.tab.profile")}
            </span>
          </button>
          {expandedSections.has(TAB_PROFILE) && (
            <div className="umv2-section-body">
              {detailLoading ? (
                <div className="umv2-sheet-loading">
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span>{t("common.loading")}</span>
                </div>
              ) : (
                <UserForm
                  mode="update"
                  formData={updateFormData}
                  errors={errors}
                  loading={detailLoading}
                  onInputChange={handleUpdateInputChange}
                  onSubmit={handleUpdateSubmit}
                  selectedUser={updateFormData}
                  onVerificationComplete={async () => {
                    setShowPhoneVerificationPopup(false);
                    if (detailUser?.uuid)
                      await loadDetailForUser(detailUser.uuid);
                  }}
                />
              )}
            </div>
          )}
        </div>

        {/* Additional Details section */}
        <div className="umv2-section">
          <button
            className={`umv2-section-header${expandedSections.has(TAB_DETAILS) ? " expanded" : ""}`}
            onClick={() => toggleSection(TAB_DETAILS)}
            aria-expanded={expandedSections.has(TAB_DETAILS)}
          >
            <FontAwesomeIcon
              icon={
                expandedSections.has(TAB_DETAILS)
                  ? faChevronDown
                  : faChevronRight
              }
              className="umv2-section-chevron"
            />
            <span className="umv2-section-title">
              {t("usermgmt.tab.additionalDetails")}
            </span>
          </button>
          {expandedSections.has(TAB_DETAILS) && (
            <div className="umv2-section-body">
              {detailLoading ? (
                <div className="umv2-sheet-loading">
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span>{t("common.loading")}</span>
                </div>
              ) : (
                <UserDetails
                  userDetails={userDetails}
                  loading={detailLoading}
                  onInputChange={handleDetailsInputChange}
                  onSubmit={handleDetailsSubmit}
                />
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSideSheet = () => (
    <div
      className={`umv2-sheet-backdrop${areAnnouncementsVisible ? " with-announcements" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label={`User details: ${detailUser?.firstName ?? ""} ${detailUser?.lastName ?? ""}`}
      onClick={(e) => {
        // Close when clicking directly on the backdrop
        if (e.target === e.currentTarget) handleCloseSheet();
      }}
    >
      <div className="umv2-sheet open" onClick={(e) => e.stopPropagation()}>
        {/* Sheet header */}
        <div className="umv2-sheet-header">
          <span className="umv2-sheet-title">
            {detailUser
              ? `${detailUser.firstName} ${detailUser.lastName}`
              : t("usermgmt.detail.emptyState")}
          </span>
          <button
            className="umv2-sheet-close"
            onClick={handleCloseSheet}
            aria-label="Close panel"
            title="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {renderSideSheetBody()}
      </div>
    </div>
  );

  // ── Solo profile view (non-owner member users) ─────────────────────────────

  if (!isOwnerView) {
    return (
      <div className="common-container">
        <div className="common-header">
          <h1 className="main-title">{t("usermgmt.myProfile.title")}</h1>
          <p className="common-subtitle">{t("usermgmt.myProfile.subtitle")}</p>
        </div>

        <div className="um-solo-pane um-sections-layout">
          {detailError && (
            <div
              className="common-message error"
              style={{ margin: "1rem 1.5rem 0" }}
            >
              <FontAwesomeIcon icon={faInfoCircle} />
              <span>{detailError}</span>
            </div>
          )}
          {prominentWarning && (
            <div style={{ padding: "0 1.5rem" }}>{prominentWarning}</div>
          )}

          {/* Profile Section */}
          <div className="um-section">
            <button
              className={`um-section-header${expandedSections.has(TAB_PROFILE) ? " expanded" : ""}`}
              onClick={() => toggleSection(TAB_PROFILE)}
              aria-expanded={expandedSections.has(TAB_PROFILE)}
            >
              <FontAwesomeIcon
                icon={
                  expandedSections.has(TAB_PROFILE)
                    ? faChevronDown
                    : faChevronRight
                }
                className="um-section-chevron"
              />
              <span className="um-section-title">
                {t("usermgmt.tab.profile")}
              </span>
            </button>
            {expandedSections.has(TAB_PROFILE) && (
              <div className="um-section-body">
                <UserForm
                  mode="update"
                  formData={updateFormData}
                  errors={errors}
                  loading={detailLoading}
                  onInputChange={handleUpdateInputChange}
                  onSubmit={handleUpdateSubmit}
                  selectedUser={updateFormData}
                  onVerificationComplete={async () => {
                    setShowPhoneVerificationPopup(false);
                    await loadDetailForUser(loggedInUser?.uuid);
                  }}
                />
              </div>
            )}
          </div>

          {/* Additional Details Section */}
          <div className="um-section">
            <button
              className={`um-section-header${expandedSections.has(TAB_DETAILS) ? " expanded" : ""}`}
              onClick={() => toggleSection(TAB_DETAILS)}
              aria-expanded={expandedSections.has(TAB_DETAILS)}
            >
              <FontAwesomeIcon
                icon={
                  expandedSections.has(TAB_DETAILS)
                    ? faChevronDown
                    : faChevronRight
                }
                className="um-section-chevron"
              />
              <span className="um-section-title">
                {t("usermgmt.tab.additionalDetails")}
              </span>
            </button>
            {expandedSections.has(TAB_DETAILS) && (
              <div className="um-section-body">
                <UserDetails
                  userDetails={userDetails}
                  loading={detailLoading}
                  onInputChange={handleDetailsInputChange}
                  onSubmit={handleDetailsSubmit}
                />
              </div>
            )}
          </div>
        </div>

        {showPhoneVerificationPopup && (
          <PhoneChangeVerificationPopup
            user={updateFormData}
            onClose={() => setShowPhoneVerificationPopup(false)}
            onVerificationComplete={async () => {
              setShowPhoneVerificationPopup(false);
              await loadDetailForUser(loggedInUser?.uuid);
            }}
          />
        )}
      </div>
    );
  }

  // ── Owner / admin: full-width list + side sheet ────────────────────────────

  return (
    <div className="common-container umv2-container">
      {/* Page header */}
      <div className="common-header um-page-header umv2-page-header">
        <h1 className="main-title">{t("usermgmt.title")}</h1>
        <p className="common-subtitle">{t("usermgmt.subtitle")}</p>
      </div>

      {/* Stat strip */}
      <div className="um-stat-strip">
        <div className="um-stat">
          <FontAwesomeIcon icon={faUsers} />
          <span>
            {subscriptionInfo.loading
              ? "…"
              : `${subscriptionInfo.currentUsers ?? users.length} / ${subscriptionInfo.maxUsers ?? "–"}`}
          </span>
          <label>{t("usermgmt.stats.totalUsers")}</label>
        </div>
        <div className="um-stat">
          <FontAwesomeIcon icon={faUserShield} />
          <span>
            {isPlatformAdmin()
              ? t("usermgmt.role.platformAdmin")
              : isAccountAdmin()
                ? t("usermgmt.role.accountAdmin")
                : isGroupAdmin()
                  ? t("usermgmt.role.groupAdmin")
                  : t("usermgmt.role.member")}
          </span>
          <label>{t("usermgmt.stats.yourRole")}</label>
        </div>
        <div className="um-stat">
          <FontAwesomeIcon icon={faLayerGroup} />
          <span>
            {subscriptionInfo.planName
              ? subscriptionInfo.planName
              : hasActiveSubscription
                ? t("usermgmt.stats.planActive")
                : t("usermgmt.stats.planNone")}
          </span>
          <label>{t("usermgmt.stats.plan")}</label>
        </div>
      </div>

      {/* Toolbar */}
      <div className="umv2-toolbar">
        <UserListControls
          refreshing={listRefreshing}
          loading={listLoading}
          onRefresh={async () => {
            setListRefreshing(true);
            await fetchUsers();
            setListRefreshing(false);
          }}
          userCount={sortedUsers.length}
        />
      </div>

      {/* Full-width user list */}
      <div className="umv2-user-list">
        {/* Column header */}
        <div className="umv2-list-header" aria-hidden="true">
          <span>{t("usermgmt.list.col.name")}</span>
          <span>{t("usermgmt.list.col.email")}</span>
          <span>{t("usermgmt.list.col.phone")}</span>
          <span>{t("usermgmt.list.col.role")}</span>
          <span>{t("usermgmt.list.col.status")}</span>
          <span>{t("usermgmt.list.col.actions")}</span>
        </div>

        {/* Loading state */}
        {listLoading && (
          <div className="umv2-list-loading">
            <LoadingSpinner message={t("usermgmt.list.loading")} />
          </div>
        )}

        {/* Empty state */}
        {!listLoading && sortedUsers.length === 0 && (
          <div className="umv2-list-empty" role="status">
            <FontAwesomeIcon icon={faUsers} />
            <span>{t("usermgmt.list.noMembers")}</span>
          </div>
        )}

        {/* User rows */}
        {!listLoading &&
          sortedUsers.map((user) => {
            const isOwner = accountOwners[user.uuid] || false;
            const isActive = user.active === 1 || user.active === true;
            // Treat any falsy value (false, null, undefined, 0) as unverified
            const isUnverified = !user.verified;
            const isSelected = rosterSelectedUser?.uuid === user.uuid;
            const isMember = !isOwner;
            const isResending = resendingFor === user.uuid;

            return (
              <div
                key={user.uuid}
                className={[
                  "umv2-user-row",
                  isSelected ? "selected" : "",
                  isUnverified ? "unverified" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                role="button"
                tabIndex={isUnverified ? -1 : 0}
                aria-selected={isSelected}
                onClick={() => handleSelectUser(user)}
                onKeyDown={(e) => {
                  if (!isUnverified && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    handleSelectUser(user);
                  }
                }}
              >
                {/* Name + group */}
                <div className="umv2-col umv2-col-name">
                  <span className="umv2-user-name">
                    {user.firstName} {user.lastName}
                  </span>
                </div>

                {/* Email */}
                <div className="umv2-col umv2-col-email">
                  <span className="umv2-cell-text" title={user.email}>
                    {user.email || (
                      <span className="umv2-cell-text muted">—</span>
                    )}
                  </span>
                </div>

                {/* Phone */}
                <div className="umv2-col umv2-col-phone">
                  <span className="umv2-cell-text">
                    {user.phone ? (
                      formatPhone(user.phone)
                    ) : (
                      <span className="umv2-cell-text muted">—</span>
                    )}
                  </span>
                </div>

                {/* Role badges */}
                <div className="umv2-col umv2-col-roles">
                  {isOwner ? (
                    <span className="common-badge owner">
                      {t("usermgmt.card.accountAdminBadge")}
                    </span>
                  ) : (
                    <span className="common-badge member">
                      {t("usermgmt.role.member")}
                    </span>
                  )}
                </div>

                {/* Status badge */}
                <div className="umv2-col umv2-col-status">
                  <span
                    className={`common-badge status ${
                      isUnverified
                        ? "unverified"
                        : isActive
                          ? "active"
                          : "inactive"
                    }`}
                  >
                    {isUnverified
                      ? t("usermgmt.card.unverified")
                      : isActive
                        ? t("usermgmt.card.active")
                        : t("usermgmt.card.inactive")}
                  </span>
                </div>

                {/* Actions */}
                <div
                  className="umv2-col umv2-col-actions"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="umv2-action-menu">
                    <button
                      className="umv2-action-menu-btn"
                      onClick={() =>
                        setOpenDropdownUuid(
                          openDropdownUuid === user.uuid ? null : user.uuid,
                        )
                      }
                      disabled={actionInProgress}
                      aria-haspopup="true"
                      aria-expanded={openDropdownUuid === user.uuid}
                      title="Actions"
                    >
                      {t("usermgmt.card.actionsBtn")}{" "}
                      <FontAwesomeIcon icon={faChevronDown} />
                    </button>

                    {openDropdownUuid === user.uuid && (
                      <div className="umv2-action-dropdown" role="menu">
                        {/* Edit Profile */}
                        <button
                          className="umv2-dropdown-item"
                          role="menuitem"
                          onClick={() => {
                            handleSelectUser(user);
                            setOpenDropdownUuid(null);
                          }}
                        >
                          <FontAwesomeIcon icon={faInfoCircle} />
                          {t("usermgmt.card.viewProfile")}
                        </button>

                        {isMember && (
                          <>
                            {/* Configure Simulation Profile */}
                            <button
                              className="umv2-dropdown-item"
                              role="menuitem"
                              onClick={() => {
                                selectUser(user);
                                setOpenDropdownUuid(null);
                                setHeroContent("Simulation Profile");
                              }}
                            >
                              <FontAwesomeIcon icon={faSlidersH} />
                              {t("usermgmt.card.configSimProfile")}
                            </button>

                            {/* Monitor Activity */}
                            <button
                              className="umv2-dropdown-item"
                              role="menuitem"
                              onClick={() => {
                                setOpenDropdownUuid(null);
                                handleMonitorActivity(user);
                              }}
                            >
                              <FontAwesomeIcon icon={faEye} />
                              {t("usermgmt.card.monitorActivity")}
                            </button>

                            {/* Deactivate / Activate */}
                            <button
                              className="umv2-dropdown-item"
                              role="menuitem"
                              onClick={() => {
                                handleToggleUserStatus(user);
                                setOpenDropdownUuid(null);
                              }}
                            >
                              <FontAwesomeIcon
                                icon={isActive ? faToggleOff : faToggleOn}
                              />
                              {isActive
                                ? t("usermgmt.card.deactivate")
                                : t("usermgmt.card.activate")}
                            </button>

                            {/* Resend verification — only for unverified users */}
                            {isUnverified && (
                              <button
                                className="umv2-dropdown-item"
                                role="menuitem"
                                onClick={() => {
                                  handleResendVerification(user);
                                  setOpenDropdownUuid(null);
                                }}
                                disabled={isResending}
                              >
                                {isResending ? (
                                  <FontAwesomeIcon icon={faSpinner} spin />
                                ) : (
                                  <FontAwesomeIcon icon={faEnvelopeOpenText} />
                                )}
                                {isResending
                                  ? t("usermgmt.resend.sending")
                                  : t("usermgmt.resend.resendEmail")}
                              </button>
                            )}

                            <div className="umv2-dropdown-divider" />

                            {/* Delete */}
                            <button
                              className="umv2-dropdown-item danger"
                              role="menuitem"
                              onClick={() => {
                                handleDeleteUser(user);
                                setOpenDropdownUuid(null);
                              }}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                              {t("usermgmt.card.delete")}
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

        {/* Add Member footer */}
        {(canCreateUsers() || isDemoSeat) &&
          subscriptionInfo.maxUsers !== 1 && (
            <div className="umv2-list-footer">
              <button
                className="common-button primary"
                onClick={handleOpenAddSheet}
                disabled={
                  subscriptionInfo.isAtLimit ||
                  !hasActiveSubscription ||
                  isDemoSeat
                }
                title={
                  isDemoSeat
                    ? t("common.notAvailableDemo")
                    : subscriptionInfo.isAtLimit
                      ? t("usermgmt.create.atLimit")
                      : !hasActiveSubscription
                        ? t("usermgmt.create.noSubscription")
                        : undefined
                }
              >
                <FontAwesomeIcon icon={faUserPlus} />
                {t("usermgmt.roster.addMember")}
              </button>
              <button
                type="button"
                className="umv2-new-member-info-btn"
                onClick={() => setNewMemberInfo((p) => ({ ...p, open: true }))}
                title="What happens after you add a member?"
              >
                <FontAwesomeIcon icon={faInfoCircle} />
                {t("usermgmt.roster.newMemberInfoBtn")}
              </button>
            </div>
          )}
      </div>

      {/* Side sheet — rendered when a row is selected */}
      {sideSheetOpen && detailUser && renderSideSheet()}

      {/* Phone verification popup */}
      {showPhoneVerificationPopup && (
        <PhoneChangeVerificationPopup
          user={updateFormData}
          onClose={() => setShowPhoneVerificationPopup(false)}
          onVerificationComplete={async () => {
            setShowPhoneVerificationPopup(false);
            if (detailUser?.uuid) await loadDetailForUser(detailUser.uuid);
            toast.success(t("usermgmt.toast.phoneUpdated"), { duration: 5000 });
          }}
        />
      )}

      {/* Add Member sheet */}
      <AddMemberSheet
        isOpen={showAddSheet}
        onClose={() => setShowAddSheet(false)}
        subscriptionInfo={subscriptionInfo}
        hasActiveSubscription={hasActiveSubscription}
        formData={createFormData}
        errors={createErrors}
        loading={createLoading}
        availableGroups={availableGroups}
        groupsLoading={groupsLoading}
        groupsError={groupsError}
        onInputChange={handleCreateInputChange}
        onSubmit={handleCreateSubmit}
        onRefreshGroups={loadAvailableGroups}
        onNavigateBilling={() => {
          setShowAddSheet(false);
          setHeroContent("Account & Billing");
        }}
      />

      {/* New member info modal */}
      <NewMemberInfoModal
        isOpen={newMemberInfo.open}
        onClose={() => setNewMemberInfo((p) => ({ ...p, open: false }))}
        memberName={newMemberInfo.name}
        memberEmail={newMemberInfo.email}
      />

      {/* Confirmation modal */}
      <ConfirmationModal
        isOpen={confirmModal.open}
        onClose={() => setConfirmModal((p) => ({ ...p, open: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        details={confirmModal.details}
        confirmText={confirmModal.confirmText}
        cancelText={t("common.cancel")}
        type={confirmModal.type}
      />
    </div>
  );
}

export default MemberUserInfo_v2;
